/**
 * FishingCatchNotification — transient toast popup for fish catches.
 *
 * Shows a brief notification when any player catches a fish, displaying
 * the fish name, rarity, weight, and points. Auto-clears after 5s
 * (handled by GmEventService).
 *
 * @see docs/design/gm/GM_EVENT_PHASE4_SPEC.md §3.2
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

const RARITY_COLORS: Record<string, string> = {
  common: COLORS.textSecondary,
  uncommon: COLORS.success,
  rare: COLORS.textAccent,
  epic: '#a855f7',
  legendary: '#f59e0b',
};

export const FishingCatchNotification: React.FC = () => {
  const { fishingCatch } = useGmEvent();

  if (!fishingCatch) return null;

  const rarityColor = RARITY_COLORS[fishingCatch.rarity] ?? COLORS.textSecondary;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 120,
    right: 20,
    padding: '8px 14px',
    background: COLORS.surface,
    border: `1px solid ${rarityColor}`,
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    fontFamily: FONTS.ui,
    zIndex: 100,
    pointerEvents: 'none',
    animation: 'fadeIn 0.3s ease-out',
    maxWidth: 280,
  };

  return (
    <div style={containerStyle}>
      <span style={{ fontSize: 13, fontWeight: 'bold', color: rarityColor }}>
        {fishingCatch.fishName} ({fishingCatch.rarity})
      </span>
      <span style={{ fontSize: 11, color: COLORS.textSecondary }}>
        {fishingCatch.weight.toFixed(1)} kg — +{fishingCatch.points} pts
      </span>
    </div>
  );
};
